// Условия таблицы «Контроль» в адресе страницы (приёмка 0.3.1, T041): имена — как у запроса
// control.items, чтобы «Главная» ссылалась прямо: /control?asOf=today&group=overdue. Значения
// по умолчанию (view=open, asOf=sheet, sort=source) в адрес не пишутся. 005 (FR-011): фильтр
// «Ответственный» — запись справочника, responsibleId; карточка записи в «Справочнике» ведёт
// сюда же: /control?responsibleId=….
import { CONTROL_SEARCH_MAX, asOfModes, controlGroupFilters, controlSorts } from '@kp/contracts';
import type { AsOfMode, ControlGroupFilter, ControlSort, RouteQuery } from '@kp/contracts';

export type ControlView = 'open' | 'aiClosed' | 'markClosed';

export interface ControlSearch {
  /** open — открытые (по умолчанию), aiClosed — закрытые ИИ, markClosed — отметкой выполнения (010). */
  view?: ControlView;
  /** sheet — по листу недели (по умолчанию), today — на сегодня. */
  asOf?: AsOfMode;
  /** Группы листа через запятую: overdue,burning. Только у открытых. */
  group?: string;
  /** Запись справочника, упомянутая в пункте: фильтр «Ответственный» (005, FR-011). */
  responsibleId?: string;
  documentId?: string;
  /** Срок не позже даты: 2026-10-20. */
  dueBefore?: string;
  q?: string;
  /** source — как в протоколе (по умолчанию), due — по сроку. */
  sort?: ControlSort;
}

/** Фильтр «Статус» по группам листа (те же значения у полосы показателей «Главной»). */
export const controlGroupFilterLabels = {
  overdue: 'Просрочено',
  burning: 'Горит',
  clarify: 'Уточнить',
  aiReview: 'Проверить (ИИ)',
  none: 'Без цвета',
} as const satisfies Record<ControlGroupFilter, string>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function oneOf<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** Группы из адреса: известные, без повторов, в порядке листа. */
export function parseGroups(value: unknown): ControlGroupFilter[] {
  if (typeof value !== 'string') return [];
  const wanted = new Set(value.split(',').map((part) => part.trim()));
  return controlGroupFilters.filter((group) => wanted.has(group));
}

export function parseControlSearch(search: Record<string, unknown>): ControlSearch {
  const result: ControlSearch = {};
  const view = oneOf(['open', 'aiClosed', 'markClosed'] as const, search.view);
  if (view) result.view = view;
  const asOf = oneOf(asOfModes, search.asOf);
  if (asOf) result.asOf = asOf;
  // Статус у закрытых ИИ и отметкой один — фильтра «Статус» там нет.
  const groups = view === 'aiClosed' || view === 'markClosed' ? [] : parseGroups(search.group);
  if (groups.length > 0) result.group = groups.join(',');
  if (typeof search.responsibleId === 'string' && UUID.test(search.responsibleId)) {
    result.responsibleId = search.responsibleId;
  }
  if (typeof search.documentId === 'string' && UUID.test(search.documentId)) {
    result.documentId = search.documentId;
  }
  if (typeof search.dueBefore === 'string' && DATE.test(search.dueBefore)) {
    result.dueBefore = search.dueBefore;
  }
  // Число в адресе (?q=12) роутер читает как число: поиск по номеру пункта — тоже строка.
  const q = typeof search.q === 'number' ? String(search.q) : search.q;
  if (typeof q === 'string' && q.trim() !== '' && q.trim().length <= CONTROL_SEARCH_MAX) {
    result.q = q;
  }
  const sort = oneOf(controlSorts, search.sort);
  if (sort) result.sort = sort;
  return result;
}

/** Вид открытых пунктов: у закрытых (ИИ или отметкой) фильтра «Статус» нет. */
function isOpenView(view: ControlView | undefined): boolean {
  return view === undefined || view === 'open';
}

/** Условия для адреса: без пустых значений и значений по умолчанию, без «Статуса» у закрытых ИИ. */
export function normalizeControlSearch(search: ControlSearch): ControlSearch {
  const result: ControlSearch = {};
  if (search.view === 'aiClosed' || search.view === 'markClosed') result.view = search.view;
  if (search.asOf === 'today') result.asOf = 'today';
  if (search.group && isOpenView(search.view)) result.group = search.group;
  if (search.responsibleId) result.responsibleId = search.responsibleId;
  if (search.documentId) result.documentId = search.documentId;
  if (search.dueBefore) result.dueBefore = search.dueBefore;
  if (search.q) result.q = search.q;
  if (search.sort === 'due') result.sort = 'due';
  return result;
}

/** Поиск и фильтры сужают список: «Ничего не найдено» вместо «Пунктов нет». */
export function hasControlFilters(search: ControlSearch): boolean {
  return Boolean(
    (search.q && search.q.trim() !== '') ||
    (isOpenView(search.view) && search.group) ||
    search.responsibleId ||
    search.documentId ||
    search.dueBefore,
  );
}

/** Сброс фильтров оставляет вид, дату отсчёта и сортировку: они не сужают список. */
export function withoutFilters(search: ControlSearch): ControlSearch {
  return normalizeControlSearch({ view: search.view, asOf: search.asOf, sort: search.sort });
}

/** Запрос к серверу — те же условия, что в адресе. */
export function controlQuery(search: ControlSearch): RouteQuery<'control.items'> {
  const view = search.view ?? 'open';
  const q = search.q?.trim();
  return {
    view,
    asOf: search.asOf ?? 'sheet',
    sort: search.sort ?? 'source',
    ...(q ? { q } : {}),
    ...(view === 'open' && search.group ? { group: search.group } : {}),
    ...(search.responsibleId ? { responsibleId: search.responsibleId } : {}),
    ...(search.documentId ? { documentId: search.documentId } : {}),
    ...(search.dueBefore ? { dueBefore: search.dueBefore } : {}),
  };
}
